import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  fetchAdminContacts,
  updateContactStatus,
  deleteContact,
  selectAdminContacts,
  selectAdminContactsLoading
} from '../../redux/slices/adminSlice';

const AdminContacts = () => {
  const dispatch = useDispatch();
  const contacts = useSelector(selectAdminContacts);
  const loading = useSelector(selectAdminContactsLoading);

  const [viewingId, setViewingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    dispatch(fetchAdminContacts());
  }, [dispatch]);

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      await dispatch(updateContactStatus({ id: contactId, status: newStatus })).unwrap();
      toast.success('Contact status updated successfully');
      setViewingId(null);
    } catch (err) {
      toast.error(err || 'Failed to update contact');
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteContact(id)).unwrap();
      toast.success('Contact deleted successfully');
      setShowDeleteConfirm(null);
    } catch (err) {
      toast.error(err || 'Failed to delete contact');
    }
  };

  const filteredContacts = (contacts || []).filter(contact => {
    if (!contact) return false;
    const matchesSearch = !searchTerm ||
      contact.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
    const matchesType = filterType === 'all' || contact.inquiryType === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">📞 Manage Contacts</h1>
        <p className="text-gray-600">View, respond to, and manage customer inquiries</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Contacts</p>
              <p className="text-3xl font-bold text-gray-900">{contacts?.length || 0}</p>
            </div>
            <div className="text-4xl">📞</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">New Inquiries</p>
              <p className="text-3xl font-bold text-gray-900">
                {contacts?.filter(c => c.status === 'new')?.length || 0}
              </p>
            </div>
            <div className="text-4xl">🆕</div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Contacted</p>
              <p className="text-3xl font-bold text-gray-900">
                {contacts?.filter(c => c.status === 'contacted')?.length || 0}
              </p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="🔍 Search contacts..."
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="buying">Buying</option>
            <option value="selling">Selling</option>
            <option value="investment">Investment</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>

      {/* Contacts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 animate-pulse">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-3"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
              <div className="h-3 bg-gray-300 rounded mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-5/6"></div>
            </div>
          ))}
        </div>
      ) : filteredContacts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContacts.map((contact, index) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
            >
              {viewingId === contact.id ? (
                // View Mode (Expanded)
                <div className="space-y-4">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600 mr-3">
                      {(contact.firstName?.[0] || 'U')}{(contact.lastName?.[0] || '')}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{contact.firstName} {contact.lastName}</h4>
                      <p className="text-sm text-gray-600">{contact.email}</p>
                      <p className="text-xs text-gray-500">{contact.phone}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-600">Inquiry Type</p>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${
                        contact.inquiryType === 'buying' ? 'bg-green-100 text-green-800' :
                        contact.inquiryType === 'selling' ? 'bg-blue-100 text-blue-800' :
                        contact.inquiryType === 'investment' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {contact.inquiryType || 'General'}
                      </span>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium text-gray-600">Subject</p>
                      <p className="text-sm text-gray-900 mt-1">{contact.subject || 'No subject'}</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-gray-600">Message</p>
                      <p className="text-sm text-gray-900 mt-1 italic">"{contact.message || 'No message'}"</p>
                    </div>

                    <div>
                      <p className="text-xs font-medium text-gray-600">Status</p>
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-1 ${
                        contact.status === 'new' ? 'bg-red-100 text-red-800' :
                        contact.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {contact.status?.toUpperCase() || 'NEW'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-3 border-t">
                    <button
                      onClick={() => handleStatusUpdate(contact.id, 'contacted')}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      ✅ Contacted
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(contact.id, 'closed')}
                      className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                    >
                      ✔️ Close
                    </button>
                  </div>
                  <button
                    onClick={() => setViewingId(null)}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                  >
                    Close
                  </button>
                </div>
              ) : (
                // Card Preview Mode
                <>
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-xl font-bold text-blue-600 mr-3">
                      {(contact.firstName?.[0] || 'U')}{(contact.lastName?.[0] || '')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-900 truncate">{contact.firstName} {contact.lastName}</h4>
                      <p className="text-sm text-gray-600 truncate">{contact.email}</p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                      contact.status === 'new' ? 'bg-red-100 text-red-800' :
                      contact.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {contact.status?.toUpperCase() || 'NEW'}
                    </span>
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ml-2 ${
                      contact.inquiryType === 'buying' ? 'bg-green-100 text-green-800' :
                      contact.inquiryType === 'selling' ? 'bg-blue-100 text-blue-800' :
                      contact.inquiryType === 'investment' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {contact.inquiryType || 'General'}
                    </span>
                  </div>

                  <p className="text-gray-600 mb-4 text-sm italic line-clamp-2">
                    "{contact.message?.substring(0, 80) || 'No message'}..."
                  </p>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingId(contact.id)}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      👁️ View
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(contact.id)}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
          <div className="text-6xl mb-4">📞</div>
          <p className="text-gray-600 text-lg">No contacts yet</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
            >
              <div className="text-center">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete Contact?</h3>
                <p className="text-gray-600 mb-6">This action cannot be undone.</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleDelete(showDeleteConfirm)}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminContacts;
